(function () {
  'use strict';
  if (window.__sentoV2) return;
  window.__sentoV2 = true;

  document.documentElement.classList.add('sento-ui');

  const bar = document.createElement('div');
  bar.className = 'sento-bar';
  bar.setAttribute('aria-hidden', 'true');
  document.body.prepend(bar);

  const onScroll = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    bar.style.transform = `scaleX(${max > 0 ? window.scrollY / max : 0})`;
    document.documentElement.classList.toggle('sento-scrolled', window.scrollY > 24);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  const targets = '.product-card, .card--product, .collection-card, .hero .text-block, .section-header, [class*="product-card"]';
  // Carousel slides only ever scroll in sideways, so a vertical reveal would leave them invisible.
  const items = [...new Set([...document.querySelectorAll(targets)])].filter(
    (el) =>
      !el.closest('slideshow-component, [class*="carousel"], [class*="slider"], [class*="scroll"]') &&
      el.getBoundingClientRect().top > window.innerHeight
  );

  items.forEach((el) => el.classList.add('sento-in'));
  setTimeout(() => items.forEach((el) => el.classList.add('sento-show')), 2500);

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const idx = items.indexOf(e.target);
        setTimeout(() => e.target.classList.add('sento-show'), Math.min(idx * 60, 400));
        io.unobserve(e.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -30px 0px' });
    items.forEach((el) => io.observe(el));
  } else {
    items.forEach((el) => el.classList.add('sento-show'));
  }

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[char]);

  const normalizeImage = (product) => {
    const image = product.image || product.featured_image || product.images?.[0];
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.url || '';
  };

  const normalizePrice = (product) => {
    const rawPrice = product.price || product.price_min || product.variants?.[0]?.price;
    if (!rawPrice) return '';
    const numericPrice = Number(String(rawPrice).replace(/[^\d.]/g, ''));
    if (!Number.isFinite(numericPrice)) return String(rawPrice);
    return `LE ${numericPrice.toFixed(2)}`;
  };

  const normalizeUrl = (product) => product.url || (product.handle ? `/products/${product.handle}` : '#');

  let popularProductsPromise = null;

  const fetchPopularProducts = () => {
    if (!popularProductsPromise) {
      popularProductsPromise = fetch('/collections/all/products.json?limit=6', {
        headers: { Accept: 'application/json' },
      })
        .then((response) => (response.ok ? response.json() : { products: [] }))
        .then((data) => data.products || [])
        .catch(() => []);
    }
    return popularProductsPromise;
  };

  const fetchSearchProducts = async (query) => {
    if (!query) return fetchPopularProducts();

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`,
        { headers: { Accept: 'application/json' } }
      );
      const data = response.ok ? await response.json() : {};
      const products = data.resources?.results?.products || [];
      if (products.length) return products;
    } catch (error) {
      // Fall back to cached all-products search below.
    }

    const popularProducts = await fetchPopularProducts();
    const normalizedQuery = query.toLowerCase();
    return popularProducts
      .filter((product) => product.title?.toLowerCase().includes(normalizedQuery))
      .slice(0, 6);
  };

  const renderSearchFallback = (container, products, query) => {
    if (!products.length) {
      container.innerHTML = `<p class="sento-search-fallback__empty">No products found for "${escapeHtml(query)}".</p>`;
      return;
    }

    const title = query ? 'Matching products' : 'Popular products';
    container.innerHTML = `
      <h4 class="sento-search-fallback__title">${title}</h4>
      <div class="sento-search-fallback__grid">
        ${products.map((product) => {
          const image = normalizeImage(product);
          return `
            <a class="sento-search-card" href="${escapeHtml(normalizeUrl(product))}">
              <span class="sento-search-card__media">
                ${image ? `<img class="sento-search-card__image" src="${escapeHtml(image)}" alt="">` : ''}
              </span>
              <span class="sento-search-card__content">
                <span class="sento-search-card__title">${escapeHtml(product.title || 'Product')}</span>
                <span class="sento-search-card__price">${escapeHtml(normalizePrice(product))}</span>
              </span>
            </a>
          `;
        }).join('')}
      </div>
    `;
  };

  const removeSearchRecentBlocks = () => {
    const modal = document.querySelector('#search-modal');
    if (!modal) return;

    modal
      .querySelectorAll('recently-viewed-products, [class*="recently-viewed"], [id*="recently-viewed"], [data-recently-viewed]')
      .forEach((node) => node.remove());

    const resultsInner = modal.querySelector('.predictive-search-results__inner');
    resultsInner?.querySelectorAll(':scope > *').forEach((node) => {
      if (node.id !== 'predictive-search-products' && !node.querySelector?.('#predictive-search-products')) node.remove();
    });
  };

  const initSearchEnhancement = () => {
    const modal = document.querySelector('#search-modal');
    const component = modal?.querySelector('predictive-search-component');
    const content = modal?.querySelector('.predictive-search-form__content');
    const input = modal?.querySelector('input[type="search"][name="q"]');
    if (!modal || !component || !content || !input || component.dataset.sentoSearchEnhanced) return;

    component.dataset.sentoSearchEnhanced = 'true';
    const fallback = document.createElement('div');
    fallback.className = 'sento-search-fallback';
    fallback.hidden = true;
    content.appendChild(fallback);

    const hasNativeCards = () =>
      Boolean(content.querySelector('#predictive-search-results .predictive-search-results__card, #predictive-search-results .resource-card'));

    let timer = 0;
    let requestId = 0;
    const updateFallback = () => {
      removeSearchRecentBlocks();
      clearTimeout(timer);
      timer = setTimeout(async () => {
        removeSearchRecentBlocks();
        const currentRequest = ++requestId;
        const query = input.value.trim();

        await new Promise((resolve) => setTimeout(resolve, query ? 350 : 0));
        removeSearchRecentBlocks();
        if (hasNativeCards()) {
          fallback.hidden = true;
          return;
        }

        fallback.hidden = false;
        fallback.innerHTML = '<p class="sento-search-fallback__empty">Loading products...</p>';
        const products = await fetchSearchProducts(query);
        if (currentRequest !== requestId) return;
        if (hasNativeCards()) {
          fallback.hidden = true;
          return;
        }
        renderSearchFallback(fallback, products, query);
      }, 180);
    };

    input.addEventListener('focus', updateFallback);
    input.addEventListener('input', updateFallback);
    modal.addEventListener('click', () => setTimeout(updateFallback, 120));

    new MutationObserver(() => {
      removeSearchRecentBlocks();
      if (hasNativeCards()) fallback.hidden = true;
    }).observe(content, { childList: true, subtree: true });

    updateFallback();
  };

  initSearchEnhancement();
  document.addEventListener('shopify:section:load', initSearchEnhancement);

  const isProductPage = () => {
    const main = document.querySelector('#MainContent');
    return location.pathname.includes('/products/') || main?.dataset.template?.includes('product');
  };

  // Each chart's numbers are transcribed from the actual size-chart graphic the merchant
  // embedded in that product's own description (not always in the photo gallery — some
  // live only inside the description body) — not a generic guess.
  const HOODIE_CHART = {
    title: 'BOXFIT HOODIE SIZE CHART',
    table: {
      columns: ['Size', 'Width', 'Length', 'Weight KG'],
      rows: [
        ['M', '59', '63', '50-70'],
        ['L', '62', '65', '70-85'],
        ['XL', '65', '67', '85-100'],
      ],
    },
    extraTables: [
      {
        label: 'Size recommendation by height',
        columns: ['Size', 'Height (cm)'],
        rows: [
          ['M', 'Under 170'],
          ['L', '170 - 180'],
          ['XL', 'Above 180'],
        ],
      },
    ],
    note: 'Measurements may vary slightly.',
  };

  const WIDE_LEG_CHART = {
    title: 'WIDE LEG SIZE CHART',
    table: {
      columns: ['Size', 'Waist', 'Thigh', 'Rise', 'Length'],
      rows: [
        ['M', '34', '32', '35', '104'],
        ['L', '36', '34', '37', '107'],
        ['XL', '38', '36', '39', '110'],
      ],
    },
    extraTables: [
      {
        label: 'For women',
        columns: ['Size', 'Height (cm)', 'Weight (kg)'],
        rows: [
          ['M', '150 - 165', '60 - 70'],
          ['L', '165 - 175', '70 - 90'],
          ['XL', 'Above 175', '90 - 110'],
        ],
      },
      {
        label: 'For men',
        columns: ['Size', 'Height (cm)', 'Weight (kg)'],
        rows: [
          ['M', '150 - 170', '60 - 70'],
          ['L', '170 - 180', '70 - 90'],
          ['XL', 'Above 180', '90 - 110'],
        ],
      },
    ],
    note: "Choose the size that matches your height; if weight and height don't agree, go by height.",
  };

  const CHARLESTON_LEGGING_CHART = {
    title: 'CHARLESTON LEGGING SIZE CHART',
    table: {
      columns: ['Size', 'Waist', 'Hip', 'Length', 'Weight KG'],
      rows: [
        ['M', '29', '41', '102', '45-60'],
        ['L', '32', '43', '104', '60-75'],
        ['XL', '35', '45', '106', '75-90'],
        ['2XL', '37', '47', '108', '90-105'],
        ['3XL', '39', '49', '110', '105-120'],
      ],
    },
    note: 'Measurements may vary slightly.',
  };

  const SLIMFIT_LEGGING_CHART = {
    title: 'LEGGING SIZE CHART',
    table: {
      columns: ['Size', 'Waist', 'Hip', 'Length', 'Weight KG'],
      rows: [
        ['M', '29', '41', '93', '45-60'],
        ['L', '32', '43', '95', '60-75'],
        ['XL', '35', '45', '98', '75-90'],
        ['2XL', '37', '47', '102', '90-105'],
        ['3XL', '39', '49', '106', '105-120'],
      ],
    },
    note: 'Measurements may vary slightly.',
  };

  const TANK_TOP_CHART = {
    title: 'TANK TOP SIZE CHART',
    table: {
      columns: ['Size', 'Width', 'Length'],
      rows: [
        ['M', '41', '66'],
        ['L', '43', '68'],
        ['XL', '45', '70'],
      ],
    },
    extraTables: [
      {
        label: 'Size recommendation by weight',
        columns: ['Size', 'Weight (kg)'],
        rows: [
          ['M', '55 - 70'],
          ['L', '70 - 85'],
          ['XL', '85 - 105'],
        ],
      },
    ],
    note: 'Material is Derby cotton and fully Lycra. Measurements may vary slightly.',
  };

  const BATMAN_LONG_SLEEVE_CHART = {
    title: 'T-SHIRT SIZE CHART',
    table: {
      columns: ['Size', 'Width', 'Length', 'Weight KG'],
      rows: [
        ['M', '46', '70', '55-70'],
        ['L', '48', '72', '70-80'],
        ['XL', '50', '74', '80-90'],
        ['2XL', '52', '76', '90-105'],
      ],
    },
    note: 'Sleeve length equals T-shirt length. Measurements may vary slightly.',
  };

  const COMPRESSION_SENTO_CHART = {
    title: 'T-SHIRT SIZE CHART',
    table: {
      columns: ['Size', 'Width', 'Length', 'Weight KG'],
      rows: [
        ['M', '46', '66', '55-70'],
        ['L', '48', '68', '70-80'],
        ['XL', '50', '70', '80-90'],
        ['2XL', '52', '72', '90-105'],
      ],
    },
    note: 'Measurements may vary slightly.',
  };

  const OVERSIZE_WOMEN_TSHIRT_CHART = {
    title: 'OVER SIZE T-SHIRT SIZE CHART',
    table: {
      columns: ['Size', 'Width', 'Length'],
      rows: [
        ['M', '50', '76'],
        ['L', '52', '78'],
        ['XL', '54', '80'],
        ['2XL', '56', '82'],
        ['3XL', '58', '84'],
      ],
    },
    extraTables: [
      {
        label: 'Size recommendation by weight',
        columns: ['Size', 'Weight (kg)'],
        rows: [
          ['M', '45 - 60'],
          ['L', '60 - 75'],
          ['XL', '75 - 85'],
          ['2XL', '85 - 100'],
          ['3XL', '100 - 115'],
        ],
      },
    ],
    note: 'Measurements may vary slightly.',
  };

  const SHORT_25_CHART = {
    title: 'SHORT OVER SIZE SIZE CHART',
    table: {
      columns: ['Size', 'Waist', 'Leg', 'Rise', 'Length', 'Weight KG'],
      rows: [
        ['M', '28', '30', '34', '52', '55-70'],
        ['L', '29', '31', '35', '53', '70-80'],
        ['XL', '30', '32', '36', '54', '80-90'],
        ['2XL', '31', '33', '37', '55', '90-105'],
        ['3XL', '32', '34', '38', '56', '105-120'],
      ],
    },
    note: 'Measurements may vary slightly.',
  };

  const TSHIRT_25_CHART = {
    title: 'OVER SIZE T-SHIRT SIZE CHART',
    table: {
      columns: ['Size', 'Width', 'Length', 'Weight KG'],
      rows: [
        ['M', '59', '73', '55-70'],
        ['L', '62', '76', '70-85'],
        ['XL', '65', '79', '85-100'],
        ['2XL', '68', '82', '100-120'],
        ['3XL', '71', '85', '120-135'],
      ],
    },
    note: 'Measurements may vary slightly.',
  };

  const WOMEN_BASIC_TOP_CHART = {
    title: 'ONE SIZE BASIC SIZE CHART',
    table: {
      columns: ['Width', 'Length', 'Weight KG'],
      rows: [['40 cm', '62 cm', 'Up to 90']],
    },
    note: 'One size only — no other sizes available. Material is Derby cotton and fully Lycra.',
  };

  const BATMAN_HALF_SLEEVE_CHART = {
    title: 'BATMAN COMPRESSION SIZE CHART',
    table: {
      columns: ['Size', 'Width', 'Length', 'Weight KG'],
      rows: [
        ['M', '46', '68', '55-70'],
        ['L', '48', '70', '70-85'],
        ['XL', '50', '72', '85-100'],
      ],
    },
    note: 'Measurements may vary slightly.',
  };

  const JERSEY_CUT_25_CHART = {
    title: 'JERSEY CUT SIZE CHART',
    table: {
      columns: ['Size', 'Width', 'Length', 'Weight KG'],
      rows: [
        ['M', '54', '70', '50-70'],
        ['L', '56', '72', '70-80'],
        ['XL', '58', '74', '80-90'],
        ['2XL', '61', '76', '90-105'],
        ['3XL', '64', '77', '105-120'],
      ],
    },
    note: 'Measurements may vary slightly.',
  };

  const JERSEY_99_CHART = {
    title: 'OVER SIZE JERSEY SIZE CHART',
    table: {
      columns: ['Size', 'Width', 'Length', 'Weight KG'],
      rows: [
        ['M', '56', '70', '55-70'],
        ['L', '59', '72', '70-85'],
        ['XL', '62', '74', '85-100'],
        ['2XL', '66', '76', '100-120'],
        ['3XL', '70', '77', '120-135'],
      ],
    },
    note: 'Measurements may vary slightly.',
  };

  // Keyed by Shopify product handle. Only products with a real, merchant-provided
  // size chart or fit text get an entry — everything else gets no trigger button
  // at all, rather than a generic chart that doesn't match the garment.
  const SIZE_CHARTS = {
    'basic-tank-top-tank-top': [TANK_TOP_CHART],
    'batman-long-sleve-compression-t-shirt-batman': [BATMAN_LONG_SLEEVE_CHART],
    'compression-sento-t-shirt-comp-nos': [COMPRESSION_SENTO_CHART],
    'jersey-cut-25-jersey-cut-25': [JERSEY_CUT_25_CHART],
    'jersey-half-sleeve-99-jersey-nos-99': [JERSEY_99_CHART],
    'oversize-sports-women-t-shirt-over-women': [OVERSIZE_WOMEN_TSHIRT_CHART],
    'short-oversize-25-short-25': [SHORT_25_CHART],
    't-shirt-baseball-oversize-25-t-shirt-25': [TSHIRT_25_CHART],
    'unisex-zipper-boxfit-hodie': [HOODIE_CHART],
    'unisex-suit-sweat-shirt-and-wide-legsuit': [HOODIE_CHART, WIDE_LEG_CHART],
    'unisex-wide-leg-wide-leg': [WIDE_LEG_CHART],
    'women-legging-charleston-charleston': [CHARLESTON_LEGGING_CHART],
    'women-legging-slim-fit-legging': [SLIMFIT_LEGGING_CHART],
    'women-basic-top-basic': [WOMEN_BASIC_TOP_CHART],
    'batman-half-sleve-compression-batman-nos': [BATMAN_HALF_SLEEVE_CHART],
  };

  const getProductHandleFromPath = () => {
    const match = location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  // Column order + display labels for size charts sourced from the `custom.size_charts`
  // product metafield (see snippets/sento-size-chart-data.liquid). Only columns that
  // have a non-empty value in at least one row of a given chart get shown.
  const METAFIELD_COLUMN_ORDER = [
    ['size', 'Size'],
    ['waist', 'Waist'],
    ['hip', 'Hip'],
    ['thigh', 'Thigh'],
    ['rise', 'Rise'],
    ['leg', 'Leg'],
    ['width', 'Width'],
    ['length', 'Length'],
    ['weight_kg', 'Weight KG'],
    ['height_cm', 'Height (cm)'],
  ];

  const getMetafieldSizeCharts = () => {
    const node = document.querySelector('#sento-size-chart-data');
    if (!node) return null;

    let chart;
    try {
      chart = JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }

    const rows = Array.isArray(chart?.rows) ? chart.rows : [];
    if (!rows.length) return null;

    const activeColumns = METAFIELD_COLUMN_ORDER.filter(([key]) =>
      rows.some((row) => String(row?.[key] ?? '').trim() !== '')
    );

    return [
      {
        title: chart.title || 'SIZE CHART',
        note: chart.note || undefined,
        table: {
          columns: activeColumns.map(([, label]) => label),
          rows: rows.map((row) => activeColumns.map(([key]) => row?.[key] || '-')),
        },
      },
    ];
  };

  const getSizeChartImage = () => {
    const node = document.querySelector('#sento-size-chart-image');
    if (!node) return null;
    try {
      const image = JSON.parse(node.textContent);
      return image?.src ? image : null;
    } catch (error) {
      return null;
    }
  };

  const renderSizeTable = (columns, rows) => `
    <div class="sento-size-chart__table-wrap">
      <table>
        <thead>
          <tr>${columns.map((column) => `<th>${escapeHtml(column)}</th>`).join('')}</tr>
        </thead>
        <tbody>
          ${rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`).join('')}
        </tbody>
      </table>
    </div>
  `;

  const renderSizeChartBlock = (chart, index) => {
    const headingId = `sento-size-chart-block-${index}`;
    const extraTables = chart.extraTables || [];

    return `
      <section class="sento-size-chart__block" aria-labelledby="${headingId}">
        <h3 class="sento-size-chart__pill" id="${headingId}">${escapeHtml(chart.title)}</h3>
        ${chart.table ? renderSizeTable(chart.table.columns, chart.table.rows) : ''}
        ${extraTables
          .map(
            (extra) => `
              <p class="sento-size-chart__pill">${escapeHtml(extra.label)}</p>
              ${renderSizeTable(extra.columns, extra.rows)}
            `
          )
          .join('')}
        ${chart.note ? `<p class="sento-size-chart__note">${escapeHtml(chart.note)}</p>` : ''}
      </section>
    `;
  };

  const buildSizeChartMarkup = (entries, image) => `
    <div class="sento-size-chart${image ? ' sento-size-chart--image' : ''}" id="sento-size-chart" role="dialog" aria-modal="true" aria-label="Size chart" hidden>
      <div class="sento-size-chart__panel" tabindex="-1">
        <button class="sento-size-chart__close" type="button" aria-label="Close size chart" data-sento-size-close>&times;</button>
        ${
          image
            ? `<img class="sento-size-chart__image" src="${escapeHtml(image.src)}" alt="${escapeHtml(image.alt || 'Size chart')}"${
                image.width ? ` width="${Number(image.width)}" height="${Number(image.height)}"` : ''
              }>`
            : `<div class="sento-size-chart__header">
                <p class="sento-size-chart__eyebrow">SENTO fit guide</p>
                <h2 class="sento-size-chart__title">SIZE CHART</h2>
                <p class="sento-size-chart__copy">Measurements below are specific to this product.</p>
              </div>
              <div class="sento-size-chart__grid">${entries.map((chart, index) => renderSizeChartBlock(chart, index)).join('')}</div>`
        }
      </div>
    </div>
  `;

  let lastFocusedElement = null;
  let activeSizeChartEntries = null;
  let activeSizeChartImage = null;

  const ensureSizeChart = () => {
    let modal = document.querySelector('#sento-size-chart');
    if (!modal && (activeSizeChartEntries?.length || activeSizeChartImage)) {
      document.body.insertAdjacentHTML('beforeend', buildSizeChartMarkup(activeSizeChartEntries || [], activeSizeChartImage));
      modal = document.querySelector('#sento-size-chart');
      modal.addEventListener('click', (event) => {
        if (event.target === modal || event.target.closest('[data-sento-size-close]')) closeSizeChart();
      });
    }
    return modal;
  };

  const openSizeChart = () => {
    const modal = ensureSizeChart();
    if (!modal) return;
    lastFocusedElement = document.activeElement;
    modal.hidden = false;
    document.body.classList.add('sento-size-chart-open');
    requestAnimationFrame(() => modal.querySelector('.sento-size-chart__panel')?.focus());
  };

  const closeSizeChart = () => {
    const modal = document.querySelector('#sento-size-chart');
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.body.classList.remove('sento-size-chart-open');
    if (lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus();
  };

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeSizeChart();
  });

  const getProductArea = () => {
    const main = document.querySelector('#MainContent');
    if (!main) return null;
    return (
      main.querySelector('.product-details') ||
      main.querySelector('product-info') ||
      main.querySelector('.product__info-container') ||
      main.querySelector('[data-product-information]') ||
      main.querySelector('form[action*="/cart/add"]')?.closest('section, .shopify-section, main') ||
      main
    );
  };

  let sizeChartObserverAttached = false;

  const initSizeChartTrigger = () => {
    if (!isProductPage()) return;

    const handle = getProductHandleFromPath();
    const entries = getMetafieldSizeCharts() || (handle ? SIZE_CHARTS[handle] : null);
    const image = getSizeChartImage();
    if (!image && !entries?.length) return;

    activeSizeChartEntries = entries || [];
    activeSizeChartImage = image;

    if (!document.querySelector('.sento-size-chart-trigger')) {
      const productArea = getProductArea();

      if (productArea) {
        const mount = document.createElement('div');
        mount.className = 'sento-size-chart-mount';

        const button = document.createElement('button');
        button.className = 'sento-size-chart-trigger';
        button.type = 'button';
        button.textContent = 'SIZE CHART';
        button.addEventListener('click', openSizeChart);
        mount.appendChild(button);

        const variantAnchors = [
          ...productArea.querySelectorAll('variant-picker, .variant-picker, .product-form__input, fieldset'),
        ].filter((node) => node.offsetParent !== null);
        const afterVariant = variantAnchors[variantAnchors.length - 1];
        const beforeButtons = productArea.querySelector('.product-form-buttons, .product-form__buttons, .buy-buttons');
        const form = productArea.querySelector('form[action*="/cart/add"]');

        if (afterVariant?.parentElement) {
          afterVariant.insertAdjacentElement('afterend', mount);
        } else if (beforeButtons?.parentElement) {
          beforeButtons.parentElement.insertBefore(mount, beforeButtons);
        } else if (form?.parentElement) {
          form.parentElement.insertBefore(mount, form);
        } else {
          productArea.appendChild(mount);
        }
      }
    }

    // Horizon re-renders the product info section (swapping in fresh markup fetched
    // from the server) whenever a variant option like color or size is picked, which
    // wipes out the button we injected above. Watch #MainContent and re-insert it
    // whenever that happens, instead of only reacting to the theme-editor reload event.
    const main = document.querySelector('#MainContent');
    if (main && !sizeChartObserverAttached) {
      sizeChartObserverAttached = true;
      let frame = 0;
      new MutationObserver(() => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(initSizeChartTrigger);
      }).observe(main, { childList: true, subtree: true });
    }
  };

  // ---- Bundles --------------------------------------------------------------
  // Every offer is its own product in Shopify and links to the product(s) it applies to.
  // A one-product entry is a quantity offer; a multi-product entry is a bundle. Both are
  // advertised independently and link to their own product page for purchasing.
  // Inactive offers render only with ?sento_bundle_preview=1.
  const bundlePreview = /[?&]sento_bundle_preview=1/.test(location.search);
  const money = (cents) =>
    `LE ${(Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const isHomePage = () => location.pathname === '/' || location.pathname === '';
  // The collection is called Offers in the admin; the menu calls it Bundles.
  const OFFERS_URL = '/collections/offers';
  const OFFERS_LABEL = 'Bundles';
  const isOffersPage = () => new RegExp(`^${OFFERS_URL}(/|$)`).test(location.pathname);

  const getBundles = () => {
    const node = document.querySelector('#sento-bundles-data');
    if (!node) return [];
    let data;
    try {
      data = JSON.parse(node.textContent);
    } catch (error) {
      return [];
    }
    if (!Array.isArray(data)) return [];
    const usable = data.filter(
      (bundle) =>
        (bundle.active || bundlePreview) &&
        Number(bundle.price) > 0 &&
        bundle.offer?.variants?.length &&
        bundle.products?.length >= 1 &&
        bundle.products.every((product) => product?.variants?.length)
    );
    // Deepest saving first, so a shop running more bundles than the home page shows still
    // leads with the one worth advertising.
    return usable.sort((a, b) => bundleTotals(b).saved - bundleTotals(a).saved);
  };

  const productLowestPrice = (product) =>
    Math.min(...product.variants.map((variant) => Number(variant.price)));

  // The bundle variant's compare-at is set from its real components, so it is exact per
  // combination; the components' own prices only stand in when it is missing.
  const bundleTotals = (bundle) => {
    const variant = bundle.offer.variants.find((entry) => entry.available) || bundle.offer.variants[0];
    const price = Number(variant.price);
    const compareAt = Number(variant.compareAt) || 0;
    const components = bundle.products.reduce((sum, product) => sum + productLowestPrice(product), 0);
    const was = compareAt > price ? compareAt : Math.max(components, price);
    return { price, was, saved: was - price };
  };

  const productNames = (products) => products.map((product) => product.title.split(' (')[0]).join(' + ');

  // Spans only, here and in the cards: both live inside anchors, which cannot hold
  // block-level children.
  const bundleThumb = (product, block) =>
    `<span class="${block}__thumb">${
      product.image ? `<img src="${escapeHtml(product.image)}" alt="" loading="lazy">` : ''
    }</span>`;

  // On a member product's page the bundle is advertised, never sold: the whole strip is a
  // link to the bundle product, which is the one place the bundle price is charged.
  const buildPromoMarkup = (bundle, handle) => {
    const { price, was, saved } = bundleTotals(bundle);
    const partners = bundle.products.filter((product) => product.handle !== handle);
    const promoName =
      bundle.products.length === 1
        ? bundle.title
        : productNames(partners.length ? partners : bundle.products);
    return `
      <a class="sento-promo" href="${escapeHtml(bundle.offer.url)}">
        <span class="sento-promo__stack">
          ${bundle.products.map((product) => bundleThumb(product, 'sento-promo')).join('')}
          ${bundle.products.length > 1 ? `<span class="sento-promo__count">${bundle.products.length}</span>` : ''}
        </span>
        <span class="sento-promo__text">
          <span class="sento-promo__label">${bundle.products.length === 1 ? 'Special offer' : 'Buy it with'}</span>
          <span class="sento-promo__name">${escapeHtml(promoName)}</span>
        </span>
        <span class="sento-promo__prices">
          <span class="sento-promo__now">${money(price)}</span>
          ${saved > 0 ? `<span class="sento-promo__was">${money(was)}</span>` : ''}
        </span>
      </a>
    `;
  };

  const buildInsideMarkup = (bundle) => {
    const { price, was, saved } = bundleTotals(bundle);
    return `
      <section class="sento-bundle sento-bundle--inside">
        <p class="sento-bundle__eyebrow">What you get in this offer</p>
        <div class="sento-bundle__items">
          ${bundle.products
            .map(
              (product, index) => `
                ${index ? '<span class="sento-bundle__plus" aria-hidden="true">+</span>' : ''}
                <div class="sento-bundle__item">
                  ${
                    product.image
                      ? `<img class="sento-bundle__image" src="${escapeHtml(product.image)}" alt="" loading="lazy">`
                      : ''
                  }
                  <div class="sento-bundle__meta">
                    <a class="sento-bundle__name" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
                    <span class="sento-bundle__itemprice">${money(productLowestPrice(product))} on its own</span>
                  </div>
                </div>`
            )
            .join('')}
        </div>
        <p class="sento-bundle__pricing">
          ${saved > 0 ? `<span class="sento-bundle__was">${money(was)}</span>` : ''}
          <span class="sento-bundle__now">${money(price)}</span>
          ${saved > 0 ? `<span class="sento-bundle__save">SAVE ${money(saved)}</span>` : ''}
        </p>
      </section>
    `;
  };

  // Offer strips sit above the quantity and add-to-cart row.
  const getPromoAnchor = (productArea) => {
    const buyButtons = productArea.querySelector('.product-form-buttons, .product-form__buttons, .buy-buttons');
    const form = productArea.querySelector('form[action*="/cart/add"]');
    const formHost = (form || buyButtons)?.closest('product-form-component');
    if (formHost?.parentElement) return formHost;
    if (form?.parentElement) return form;
    if (buyButtons?.parentElement) return buyButtons;
    return productArea.querySelector('.sento-size-chart-mount');
  };

  const initBundleOffer = () => {
    if (!isProductPage() || document.querySelector('.sento-bundle, .sento-promo:not([data-sento-qty])')) return;
    const handle = getProductHandleFromPath();
    if (!handle) return;
    // A product can belong to several bundles, so every match is advertised. Plain
    // sale-priced products are left alone: Shopify already strikes their old price out.
    const all = getBundles();
    const owned = all.find((entry) => entry.offer.handle === handle);
    const matches = all.filter((entry) => entry.products.some((product) => product.handle === handle));

    const productArea = getProductArea();
    if (!productArea) return;

    // On the offer's own page, show what is inside it instead of selling it again.
    if (owned) {
      document.body.classList.add('sento-bundle-page');
      const holder = document.createElement('div');
      holder.innerHTML = buildInsideMarkup(owned);
      const node = holder.firstElementChild;
      const form = productArea.querySelector('form[action*="/cart/add"]');
      const host = form?.closest('product-form-component') || form;
      if (host?.parentElement) host.insertAdjacentElement('afterend', node);
      else productArea.appendChild(node);
      return;
    }

    if (!matches.length) return;

    const anchor = getPromoAnchor(productArea);
    let previous = null;
    matches.forEach((bundle) => {
      const holder = document.createElement('div');
      holder.innerHTML = buildPromoMarkup(bundle, handle);
      const node = holder.firstElementChild;

      // Above the quantity and add-to-cart row, where shoppers meet it while deciding.
      if (previous) previous.insertAdjacentElement('afterend', node);
      else if (anchor?.parentElement) anchor.insertAdjacentElement('beforebegin', node);
      else productArea.appendChild(node);

      previous = node;
    });
  };

  // ---- Quantity offers (Releasit COD app) -----------------------------------
  // The COD app keeps "buy N, save X" offers in window._RSI_COD_FORM_QUANTIY_OFFERS (sic)
  // and applies them as a discount code in the cart, so the product page alone never shows
  // the reduced price. These strips show it, independent of any bundle the product is in.
  const quantityTierTotals = (tier, unitPrice) => {
    const qty = Number(tier.qty) || 0;
    const was = unitPrice * qty;
    const value = Number(tier.ds?.v) || 0;
    let saved = 0;
    if (tier.ds?.t === 'fixed') saved = value;
    else if (/percent/i.test(tier.ds?.t || '')) saved = Math.round((was * value) / 100);
    return { qty, was, saved, price: was - saved };
  };

  const buildQuantityMarkup = (tier, totals, image) => `
    <button class="sento-promo sento-promo--qty" type="button" data-sento-qty="${totals.qty}" aria-pressed="false">
      <span class="sento-promo__stack">
        <span class="sento-promo__thumb">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : ''}</span>
        <span class="sento-promo__count">${totals.qty}</span>
      </span>
      <span class="sento-promo__text">
        ${totals.saved > 0 ? `<span class="sento-promo__label">Offer · buy ${totals.qty} save ${money(totals.saved)}</span>` : ''}
        <span class="sento-promo__name">${escapeHtml(tier.title || `Buy ${totals.qty}`)}</span>
        <span class="sento-promo__each">${money(totals.price / totals.qty)} each</span>
      </span>
      <span class="sento-promo__prices">
        <span class="sento-promo__now">${money(totals.price)}</span>
        ${totals.saved > 0 ? `<span class="sento-promo__was">${money(totals.was)}</span>` : ''}
      </span>
    </button>
  `;

  const markQuantityOffer = (qty) => {
    document.querySelectorAll('[data-sento-qty]').forEach((node) => {
      node.setAttribute('aria-pressed', String(Number(node.dataset.sentoQty) === Number(qty)));
    });
  };

  // Choosing an offer only sets the quantity; the shopper then picks Add to cart or the COD order.
  // Horizon's quantity selector keeps its own count, so it is stepped with its own buttons.
  const selectQuantityOffer = (productArea, qty) => {
    const input = [...productArea.querySelectorAll('input[name="quantity"]')].find((field) => field.offsetParent);
    if (!input) return;
    const selector = input.closest('quantity-selector-component, .quantity-selector') || input.parentElement;
    const plus = selector.querySelector('.quantity-plus');
    const minus = selector.querySelector('.quantity-minus');
    for (let step = 0; step < 20 && Number(input.value) !== qty; step += 1) {
      const button = Number(input.value) < qty ? plus : minus;
      if (!button || button.disabled) break;
      button.click();
    }
    markQuantityOffer(input.value);
  };

  // The +/- buttons change the quantity too, so the highlighted offer follows it.
  let quantitySyncAttached = false;
  const syncQuantityOffers = () => {
    if (quantitySyncAttached) return;
    quantitySyncAttached = true;
    const follow = () =>
      requestAnimationFrame(() => {
        const input = [...document.querySelectorAll('#MainContent input[name="quantity"]')].find((field) => field.offsetParent);
        if (input) markQuantityOffer(input.value);
      });
    ['input', 'change'].forEach((type) =>
      document.addEventListener(type, (event) => event.target.matches?.('input[name="quantity"]') && follow())
    );
    document.addEventListener('click', (event) => event.target.closest?.('.quantity-minus, .quantity-plus') && follow());
  };

  // Quantity offers come from #sento-cod-config (live Releasit metafield, no popup).
  const ownCodForm = () => Boolean(document.getElementById('sento-cod-config'));
  const codOffers = () => {
    try {
      const offers = JSON.parse(document.getElementById('sento-cod-config')?.textContent || '{}').offers;
      if (Array.isArray(offers)) return offers;
    } catch (error) {
      // Fall through to the Releasit window copy if the snippet is missing.
    }
    return Array.isArray(window._RSI_COD_FORM_QUANTIY_OFFERS) ? window._RSI_COD_FORM_QUANTIY_OFFERS : null;
  };

  // The COD app can define its offers after this script runs, so wait for them briefly.
  const waitForQuantityOffers = async () => {
    for (let attempt = 0; attempt < 20; attempt += 1) {
      if (codOffers()) return codOffers();
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
    return [];
  };

  let quantityOffersRunning = false;
  let quantityProduct = null;

  const initQuantityOffers = async () => {
    if (!isProductPage() || quantityOffersRunning || document.querySelector('[data-sento-qty]')) return;
    const handle = getProductHandleFromPath();
    if (!handle) return;
    quantityOffersRunning = true;
    try {
      await mountQuantityOffers(handle);
    } finally {
      quantityOffersRunning = false;
    }
  };

  const mountQuantityOffers = async (handle) => {
    const offers = await waitForQuantityOffers();
    if (!offers.length) return;

    if (quantityProduct?.handle !== handle) {
      try {
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        quantityProduct = await response.json();
      } catch (error) {
        return;
      }
    }
    const product = quantityProduct;

    // Offers the Releasit app draws on the page itself (with a size/colour picker per piece) need
    // no strip; our own form only lists them inside the popup, so the strip shows them all.
    const tiers = offers
      .filter((offer) => offer.isActive && offer.type === 'quantity-offer' && (ownCodForm() || !offer.inlineOnPDP) && (offer.pIds || []).map(String).includes(String(product.id)))
      .flatMap((offer) => offer.offers || [])
      .filter((tier) => Number(tier.qty) > 1 && Number(tier.ds?.v) > 0);
    if (!tiers.length || document.querySelector('[data-sento-qty]')) return;

    const variantId = new URLSearchParams(location.search).get('variant');
    const variant =
      product.variants.find((entry) => String(entry.id) === variantId) ||
      product.variants.find((entry) => entry.available) ||
      product.variants[0];
    const unitPrice = Number(variant?.price ?? product.price);

    const productArea = getProductArea();
    if (!productArea) return;
    const anchor = getPromoAnchor(productArea);
    const bundlePromos = productArea.querySelectorAll('.sento-promo:not([data-sento-qty])');
    let previous = bundlePromos[bundlePromos.length - 1] || null;

    [{ qty: 1, title: 'Buy 1 item' }, ...tiers].forEach((tier) => {
      const totals = quantityTierTotals(tier, unitPrice);
      if (totals.qty > 1 && totals.saved <= 0) return;
      const holder = document.createElement('div');
      holder.innerHTML = buildQuantityMarkup(tier, totals, product.featured_image);
      const node = holder.firstElementChild;
      node.addEventListener('click', () => selectQuantityOffer(productArea, totals.qty));

      if (previous) previous.insertAdjacentElement('afterend', node);
      else if (anchor?.parentElement) anchor.insertAdjacentElement('beforebegin', node);
      else productArea.appendChild(node);
      previous = node;
    });

    syncQuantityOffers();
    markQuantityOffer([...productArea.querySelectorAll('input[name="quantity"]')].find((field) => field.offsetParent)?.value || 1);
  };

  // One short row per bundle, so a shop running several of them still reads as a tidy list
  // instead of leaving the laptop view looking empty around a single card.
  const dealRow = (bundle, trailing) => {
    const { price, was, saved } = bundleTotals(bundle);
    return `
      <span class="sento-deal__stack">
        ${bundle.products.map((product) => bundleThumb(product, 'sento-deal')).join('')}
        ${bundle.products.length > 1 ? `<span class="sento-deal__count">${bundle.products.length}</span>` : ''}
      </span>
      <span class="sento-deal__text">
        <span class="sento-deal__title">${escapeHtml(bundle.title)}</span>
        ${saved > 0 ? `<span class="sento-deal__save">save ${money(saved)}</span>` : ''}
      </span>
      <span class="sento-deal__prices">
        <span class="sento-deal__now">${money(price)}</span>
        ${saved > 0 ? `<span class="sento-deal__was">${money(was)}</span>` : ''}
      </span>
      ${trailing}
    `;
  };

  const buildDealMarkup = (bundle) => `
      <details class="sento-deal">
        <summary class="sento-deal__row">
          ${dealRow(bundle, '<span class="sento-deal__toggle" aria-hidden="true"></span>')}
        </summary>
        <div class="sento-deal__panel">
          <ul class="sento-deal__items">
            ${bundle.products
              .map(
                (product) => `
                  <li>
                    <a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
                    <span>${money(productLowestPrice(product))} on its own</span>
                  </li>`
              )
              .join('')}
          </ul>
          <a class="sento-deal__cta" href="${escapeHtml(bundle.offer.url)}">Get the bundle</a>
        </div>
      </details>
    `;

  const hideDuplicateBundleCards = (main, bundles) => {
    const bundleHandles = new Set(bundles.map((bundle) => bundle.offer.handle));
    const hiddenCards = new Set();

    main.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      if (link.closest('.sento-deals')) return;

      let handle;
      try {
        handle = new URL(link.href, location.origin).pathname.match(/^\/products\/([^/]+)/)?.[1];
      } catch (error) {
        return;
      }
      if (!bundleHandles.has(handle)) return;

      const card =
        link.closest('.product-card, .resource-card, [class*="product-card"]') ||
        link.closest('.product-grid__item, .resource-list__item, .grid__item, li');
      if (card && !hiddenCards.has(card)) {
        card.hidden = true;
        hiddenCards.add(card);
      }
    });
  };

  // On the home page the list follows the hero; on the Bundles collection it sits above
  // the sale product cards. Neither may sit at the top of #MainContent.
  const dealsMount = (main) => {
    if (isHomePage()) {
      const hero = [...main.children].find((child) => child.offsetHeight > 120);
      return hero ? [hero, 'afterend'] : [main, null];
    }
    const grid = main.querySelector('[class*="product-grid"], .collection__products');
    const section = grid?.closest('.shopify-section') || main.firstElementChild;
    return section ? [section, 'beforebegin'] : [main, null];
  };

  const initDeals = () => {
    if (!(isHomePage() || isOffersPage()) || document.querySelector('.sento-deals')) return;
    const bundles = getBundles();
    if (!bundles.length) return;
    const main = document.querySelector('#MainContent');
    if (!main) return;

    const holder = document.createElement('div');
    holder.innerHTML = `
      <section class="sento-deals">
        <p class="sento-deals__eyebrow">On offer now</p>
        <h2 class="sento-deals__title">Bundles</h2>
        <div class="sento-deals__list">${bundles.map(buildDealMarkup).join('')}</div>
      </section>
    `;
    const section = holder.firstElementChild;

    const [target, position] = dealsMount(main);
    if (position) target.insertAdjacentElement(position, section);
    else main.appendChild(section);

    // Keep standalone sale offers visible in the native collection grid. Only hide bundle
    // product cards there because those bundles are already rendered in the custom list.
    if (isOffersPage()) {
      hideDuplicateBundleCards(main, bundles);
    }
  };

  // ---- Offers in the header menu -------------------------------------------
  // Cloned from a real menu item, so it inherits every class and custom property the theme
  // hangs off its links instead of guessing at them.
  const mountOffersNav = () => {
    const header = document.querySelector('#header-group');
    if (!header) return;

    [header.querySelector('overflow-list.overflow-menu'), header.querySelector('.menu-drawer__menu')].forEach(
      (list) => {
        if (!list || list.querySelector('[data-sento-offers]')) return;
        const items = [...list.children].filter((child) => child.tagName === 'LI');
        // A template with a submenu would drag its whole child list along.
        const template = [...items].reverse().find((item) => item.querySelector('a') && !item.querySelector('ul, details'));
        if (!template) return;

        const item = template.cloneNode(true);
        const link = item.querySelector('a');
        item.dataset.sentoOffers = 'true';
        if (item.style.getPropertyValue('--menu-drawer-animation-index')) {
          item.style.setProperty('--menu-drawer-animation-index', String(items.length + 1));
        }
        link.href = OFFERS_URL;
        link.removeAttribute('id');
        link.removeAttribute('aria-current');
        link.classList.remove('menu-list__link--active', 'menu-drawer__menu-item--active');
        // Horizon's own hover and ref wiring belongs to the item it was cloned from.
        ['ref', 'on:focus', 'on:blur', 'on:pointerenter', 'on:pointerleave'].forEach((name) => {
          item.removeAttribute(name);
          link.removeAttribute(name);
        });
        (link.querySelector('span') || link).textContent = OFFERS_LABEL;
        list.appendChild(item);
      }
    );
  };

  let offersNavObserved = false;

  const initOffersNav = () => {
    mountOffersNav();
    const header = document.querySelector('#header-group');
    if (!header || offersNavObserved) return;
    // Horizon re-renders the header on cart and section updates, which drops our item.
    offersNavObserved = true;
    let frame = 0;
    new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(mountOffersNav);
    }).observe(header, { childList: true, subtree: true });
  };

  let offersObserved = false;

  // Horizon swaps the product info markup on variant changes, which drops the offer strips.
  const observeProductOffers = () => {
    const main = document.querySelector('#MainContent');
    if (!isProductPage() || !main || offersObserved) return;
    offersObserved = true;
    let frame = 0;
    new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        initBundleOffer();
        initQuantityOffers();
      });
    }).observe(main, { childList: true, subtree: true });
  };

  const initOffers = () => {
    initBundleOffer();
    initQuantityOffers();
    initDeals();
    initOffersNav();
    observeProductOffers();
  };

  // ---- Offer codes in the cart --------------------------------------------
  // The COD app applies quantity offers as codes named RSI_QUANTITY_<id>. Removing a code reads
  // the pill's data-discount-code, so only the visible text is renamed.
  const OFFER_CODE = /RSI_QUANTITY_\d+/g;
  const OFFER_LABEL = 'Quantity offer';

  const relabelOfferCodes = () => {
    document.querySelectorAll('.cart-items__details, .cart-discount__pill-code, [class*="cart-drawer"]').forEach((root) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (OFFER_CODE.test(node.nodeValue)) node.nodeValue = node.nodeValue.replace(OFFER_CODE, OFFER_LABEL);
        OFFER_CODE.lastIndex = 0;
      }
    });
  };

  // The COD app hides Shopify's checkout button and puts its order form only on the cart page,
  // so the drawer needs its own way there.
  const mountDrawerCheckout = () => {
    document.querySelectorAll('.cart__checkout-button').forEach((checkout) => {
      if (!checkout.closest('cart-drawer-component, .cart-drawer')) return;
      if (checkout.parentElement.querySelector('.sento-drawer-checkout')) return;
      const link = document.createElement('a');
      link.className = 'sento-drawer-checkout';
      link.href = '/cart';
      link.textContent = 'Proceed to buy';
      checkout.insertAdjacentElement('beforebegin', link);
    });
  };

  const syncCart = () => {
    relabelOfferCodes();
    mountDrawerCheckout();
  };

  const initOfferCodeLabels = () => {
    syncCart();
    let frame = 0;
    new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(syncCart);
    }).observe(document.body, { childList: true, subtree: true });
  };

  // ---- Quantity offers in the cart -----------------------------------------
  // The COD app adds an offer's discount code only when an order starts on the product page,
  // so raising a quantity in the cart never earned the offer. The codes are applied here instead.
  let offerCodesRun = null;
  let offerCodesAgain = false;

  // Resolves once the cart carries the right codes, so checkout never opens without the offer.
  const applyQuantityOfferCodes = () => {
    if (offerCodesRun) {
      offerCodesAgain = true;
      return offerCodesRun;
    }
    offerCodesRun = (async () => {
      do {
        offerCodesAgain = false;
        await applyQuantityOfferCodesOnce();
      } while (offerCodesAgain);
    })().finally(() => {
      offerCodesRun = null;
    });
    return offerCodesRun;
  };
  window.SentoOffers = { apply: applyQuantityOfferCodes };

  const applyQuantityOfferCodesOnce = async () => {
    const offers = codOffers();
    if (!Array.isArray(offers)) return;
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      const quantities = {};
      cart.items.forEach((item) => {
        quantities[item.product_id] = (quantities[item.product_id] || 0) + item.quantity;
      });

      const wanted = offers
        .filter((offer) => offer.isActive && offer.type === 'quantity-offer')
        .map((offer) => {
          const qty = (offer.pIds || []).reduce((sum, id) => sum + (quantities[id] || 0), 0);
          const best = (offer.offers || [])
            .filter((tier) => tier.ds?.d?.code && Number(tier.qty) <= qty)
            .sort((a, b) => Number(b.qty) - Number(a.qty))[0];
          return best?.ds.d.code;
        })
        .filter(Boolean);

      const current = (cart.discount_codes || []).map((discount) => discount.code);
      const next = [...current.filter((code) => !/^RSI_QUANTITY_/.test(code)), ...wanted];
      if (next.length === current.length && next.every((code) => current.includes(code))) return;

      const updated = await (
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: next.join(',') }),
        })
      ).json();
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: updated, sourceId: 'sento-offers', data: { source: 'sento-offers', itemCount: updated.item_count } },
        })
      );
    } catch (error) {
      // The cart keeps working without the offer; nothing to recover.
    }
  };

  document.addEventListener('cart:update', (event) => {
    if (event.detail?.sourceId !== 'sento-offers') applyQuantityOfferCodes();
  });
  window.addEventListener('load', () => setTimeout(applyQuantityOfferCodes, 1500));

  initSizeChartTrigger();
  initOffers();
  initOfferCodeLabels();
  document.addEventListener('shopify:section:load', () => {
    initSizeChartTrigger();
    initOffers();
  });
})();
